<template>
  <v-row>
    <!-- toolbar -->
    <v-col cols="12">
      <v-toolbar dense flat rounded color="primary" dark>
        <v-toolbar-title>Auctioneers</v-toolbar-title>

        <v-chip
          v-if="recordSource === 'new' && reportingReady"
          small
          outlined
          color="white"
          text-color="white"
          class="ml-3"
        >
          Reporting loaded in {{ reportingLoadMs }} ms
        </v-chip>

        <v-spacer />

        <v-btn
          small
          depressed
          color="white"
          class="primary--text mr-2"
          :loading="exporting"
          :disabled="loading || exporting || !auctioneerRecords.length"
          @click="exportAuctioneerReport"
        >
          <v-icon left small>mdi-file-excel</v-icon>
          Export Report
        </v-btn>

        <v-btn
          v-if="canEditAuctioneers"
          fab
          small
          depressed
          color="white"
          class="primary--text"
          :loading="loading"
          :disabled="loading"
          @click="openNewAuctioneer"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-toolbar>
    </v-col>

    <!-- controls -->
    <v-col cols="12">
      <v-card outlined>
        <v-card-title class="d-flex flex-wrap align-center">
          <v-text-field
            v-model="search"
            label="Search Auctioneers"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            clearable
            hide-details
            style="max-width: 500px"
          />

          <v-spacer />

          <v-btn-toggle v-model="recordSource" mandatory dense color="primary">
            <v-btn value="new">
              New
            </v-btn>
            <v-btn value="legacy">
              Legacy
            </v-btn>
          </v-btn-toggle>
        </v-card-title>

        <v-divider v-if="recordSource === 'new'" />

        <v-card-text v-if="recordSource === 'new'">
          <v-row dense align="center">
            <v-col cols="12" sm="4" md="3">
              <v-select
                v-model="reportYear"
                :items="reportYearOptions"
                label="Year"
                clearable
                outlined
                dense
                hide-details
              />
            </v-col>

            <v-col cols="12" sm="4" md="3">
              <v-select
                v-model="reportQuarter"
                :items="reportQuarterOptions"
                item-text="text"
                item-value="value"
                label="Quarter"
                clearable
                outlined
                dense
                hide-details
              />
            </v-col>

            <v-col cols="12" sm="4" md="3">
              <v-select
                v-model="reportMonth"
                :items="reportMonthOptions"
                item-text="text"
                item-value="value"
                label="Month"
                clearable
                outlined
                dense
                hide-details
              />
            </v-col>

            <v-col cols="12" md="3" class="d-flex justify-end">
              <v-btn
                text
                color="primary"
                :disabled="!hasActiveReportFilters"
                @click="clearReportFilters"
              >
                Clear Filters
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- table -->
    <v-col cols="12">
      <v-card outlined :loading="loading">
        <v-data-table
          :headers="currentHeaders"
          :items="currentItems"
          :search="search"
          item-key="id"
          :loading="loading"
          :items-per-page="25"
          :footer-props="{
            'items-per-page-options': [15, 25, 50, 100, -1],
            'show-current-page': true,
          }"
          no-data-text="No auctioneer records found"
          @click:row="openAuctioneer"
        >
          <!-- shared name -->
          <template v-slot:item.name="{ item }">
            <span class="font-weight-bold primary--text">
              {{ item.name }}
            </span>
          </template>

          <!-- legacy associated auctioneers -->
          <template
            v-if="recordSource === 'legacy'"
            v-slot:item.auctioneerNames="{ item }"
          >
            <div
              v-if="item.auctioneerNames.length"
              class="d-flex flex-wrap py-2"
              style="gap: 6px"
            >
              <v-chip
                v-for="name in item.auctioneerNames"
                :key="name"
                small
                outlined
              >
                {{ name }}
              </v-chip>
            </div>

            <span v-else class="grey--text text-caption">
              None assigned
            </span>
          </template>

          <!-- legacy address -->
          <template
            v-if="recordSource === 'legacy'"
            v-slot:item.address="{ item }"
          >
            {{ item.address || "--" }}
          </template>

          <!-- legacy sales -->
          <template
            v-if="recordSource === 'legacy'"
            v-slot:item.salesYtd="{ item }"
          >
            <span class="font-weight-bold">
              {{ formatCurrency(item.salesYtd || 0) }}
            </span>
          </template>

          <!-- new type -->
          <template v-if="recordSource === 'new'" v-slot:item.type="{ item }">
            <v-chip
              small
              outlined
              :color="item.type === 'company' ? 'secondary' : 'primary'"
            >
              {{ item.type === "company" ? "Company" : "Auctioneer" }}
            </v-chip>
          </template>

          <!-- new companies -->
          <template
            v-if="recordSource === 'new'"
            v-slot:item.companyNames="{ item }"
          >
            <div
              v-if="item.companyNames && item.companyNames.length"
              class="d-flex flex-wrap py-2"
              style="gap: 6px"
            >
              <v-chip
                v-for="companyName in item.companyNames"
                :key="companyName"
                small
                outlined
              >
                {{ companyName }}
              </v-chip>
            </div>

            <span v-else class="grey--text text-caption">
              --
            </span>
          </template>

          <!-- new phone -->
          <template
            v-if="recordSource === 'new'"
            v-slot:item.phoneNumber="{ item }"
          >
            {{ item.phoneNumber || "--" }}
          </template>

          <!-- new email -->
          <template
            v-if="recordSource === 'new'"
            v-slot:item.contactEmail="{ item }"
          >
            {{ item.contactEmail || "--" }}
          </template>

          <!-- new sales rep -->
          <template
            v-if="recordSource === 'new'"
            v-slot:item.salesRep="{ item }"
          >
            {{ item.salesRep || "--" }}
          </template>

          <!-- new matched ER count -->
          <template
            v-if="recordSource === 'new'"
            v-slot:item.matchedEventReportCount="{ item }"
          >
            <span class="font-weight-medium">
              {{ item.matchedEventReportCount || 0 }}
            </span>
          </template>

          <!-- new packages sold -->
          <template
            v-if="recordSource === 'new'"
            v-slot:item.packagesSold="{ item }"
          >
            <span class="font-weight-medium">
              {{ item.packagesSold || 0 }}
            </span>
          </template>

          <!-- new total sales -->
          <template
            v-if="recordSource === 'new'"
            v-slot:item.totalSales="{ item }"
          >
            <span class="font-weight-bold">
              {{ formatCurrency(item.totalSales || 0) }}
            </span>
          </template>

          <!-- new estimated commission -->
          <template
            v-if="recordSource === 'new'"
            v-slot:item.estimatedCommission="{ item }"
          >
            <span v-if="item.defaultCommissionRate !== null">
              {{ formatCurrency(item.estimatedCommission || 0) }}
            </span>
            <span v-else class="grey--text">
              --
            </span>
          </template>

          <!-- new active -->
          <template v-if="recordSource === 'new'" v-slot:item.active="{ item }">
            <v-chip small :color="item.active ? 'success' : 'grey'" dark>
              {{ item.active ? "Active" : "Inactive" }}
            </v-chip>
          </template>

          <!-- actions -->
          <template v-slot:item.actions="{ item }">
            <v-btn icon color="primary" @click.stop="openAuctioneer(item)">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </v-col>

    <!-- new auctioneer/company dialog -->
    <v-dialog v-model="newDialog" max-width="900" persistent>
      <AuctioneerDetails
        :item="newItem"
        :companies="auctioneerRecords"
        :loading="saving"
        mode="new"
        @save="createAuctioneerRecord"
        @cancel="closeNewAuctioneer"
      />
    </v-dialog>
  </v-row>
</template>

<script>
import portalsApp from "@/plugins/firebase";
import formatCurrency from "@/mixins/formatCurrency";
import { can } from "@/services/auth";
import AuctioneerDetails from "@/components/AuctioneerRecords/AuctioneerDetails.vue";
import * as XLSX from "xlsx";

export default {
  name: "AuctioneerRecordsIndex",

  components: {
    AuctioneerDetails,
  },

  mixins: [formatCurrency],

  data: () => ({
    loading: false,
    saving: false,
    exporting: false,
    reportingLoading: false,
    reportingReady: false,
    reportingLoadMs: 0,

    search: "",
    recordSource: "new",

    allEventReports: [],
    reportYear: null,
    reportQuarter: null,
    reportMonth: null,

    reportQuarterOptions: [
      { text: "Q1 (Jan - Mar)", value: 1 },
      { text: "Q2 (Apr - Jun)", value: 2 },
      { text: "Q3 (Jul - Sep)", value: 3 },
      { text: "Q4 (Oct - Dec)", value: 4 },
    ],

    reportMonthOptions: [
      { text: "January", value: 1 },
      { text: "February", value: 2 },
      { text: "March", value: 3 },
      { text: "April", value: 4 },
      { text: "May", value: 5 },
      { text: "June", value: 6 },
      { text: "July", value: 7 },
      { text: "August", value: 8 },
      { text: "September", value: 9 },
      { text: "October", value: 10 },
      { text: "November", value: 11 },
      { text: "December", value: 12 },
    ],

    // Existing legacy auctioneers collection.
    auctioneers: [],

    // New AuctioneerRecords collection.
    auctioneerRecords: [],

    newDialog: false,

    newItem: {
      type: "auctioneer",
      active: true,
      name: "",
      companyIds: [],
      phoneNumber: "",
      contactEmail: "",
      commissionEmail: "",
      defaultCommissionRate: null,
      mailingAddress: "",
      salesRep: "",
      notes: "",
    },

    legacyHeaders: [
      {
        text: "Auctioneer / Company",
        value: "name",
        sortable: true,
      },
      {
        text: "Associated Auctioneers / Companies",
        value: "auctioneerNames",
        sortable: false,
      },
      {
        text: "Mailing Address",
        value: "address",
        sortable: true,
      },
      {
        text: "Total Sales YTD",
        value: "salesYtd",
        sortable: true,
        align: "end",
      },
      {
        text: "",
        value: "actions",
        sortable: false,
        align: "end",
        width: "70px",
      },
    ],

    newHeaders: [
      {
        text: "Name",
        value: "name",
        sortable: true,
      },
      {
        text: "Type",
        value: "type",
        sortable: true,
      },
      {
        text: "Companies",
        value: "companyNames",
        sortable: false,
      },
      {
        text: "Phone",
        value: "phoneNumber",
        sortable: true,
      },
      {
        text: "Contact Email",
        value: "contactEmail",
        sortable: true,
      },
      {
        text: "Sales Rep",
        value: "salesRep",
        sortable: true,
      },
      {
        text: "ERs",
        value: "matchedEventReportCount",
        sortable: true,
        align: "end",
      },
      {
        text: "Packages",
        value: "packagesSold",
        sortable: true,
        align: "end",
      },
      {
        text: "Total Sales",
        value: "totalSales",
        sortable: true,
        align: "end",
      },
      {
        text: "Est. Commission",
        value: "estimatedCommission",
        sortable: true,
        align: "end",
      },
      {
        text: "Status",
        value: "active",
        sortable: true,
      },
      {
        text: "",
        value: "actions",
        sortable: false,
        align: "end",
        width: "70px",
      },
    ],
  }),

  computed: {
    canEditAuctioneers() {
      const user = this.$store.state.user;

      return can(user, this.$store, "editAuctioneers");
    },

    currentHeaders() {
      return this.recordSource === "new" ? this.newHeaders : this.legacyHeaders;
    },

    currentItems() {
      return this.recordSource === "new"
        ? this.newAuctioneerRows
        : this.auctioneers;
    },

    reportYearOptions() {
      const years = this.allEventReports
        .map((report) => this.getEventDateParts(report.eventDate).year)
        .filter(Boolean);

      return [...new Set(years)].sort((a, b) => b - a);
    },

    hasActiveReportFilters() {
      return Boolean(
        this.reportYear ||
        this.reportQuarter ||
        this.reportMonth
      );
    },

    filteredEventReports() {
      if (!this.hasActiveReportFilters) {
        return this.allEventReports;
      }

      return this.allEventReports.filter((report) => {
        const parts = this.getEventDateParts(report.eventDate);

        if (!parts.year || !parts.month) {
          return false;
        }

        if (
          this.reportYear &&
          parts.year !== Number(this.reportYear)
        ) {
          return false;
        }

        if (
          this.reportQuarter &&
          parts.quarter !== Number(this.reportQuarter)
        ) {
          return false;
        }

        if (
          this.reportMonth &&
          parts.month !== Number(this.reportMonth)
        ) {
          return false;
        }

        return true;
      });
    },

    newAuctioneerRows() {
      if (!this.reportingReady) {
        return this.auctioneerRecords.map((record) => ({
          ...record,
          matchedEventReportCount: 0,
          packagesSold: 0,
          totalSales: 0,
          estimatedCommission: 0,
        }));
      }

      return this.auctioneerRecords.map((record) => {
        const matchedReports = this.getMatchedEventReports(
          record,
          this.auctioneerRecords,
          this.filteredEventReports,
        );

        const totalSales = matchedReports.reduce(
          (total, report) =>
            total + (Number(report.eventReportTotal) || 0),
          0,
        );

        const packagesSold = matchedReports.reduce(
          (total, report) =>
            total + this.getPackageCount(report),
          0,
        );

        const rate =
          record.defaultCommissionRate === undefined ||
          record.defaultCommissionRate === null ||
          record.defaultCommissionRate === ""
            ? null
            : Number(record.defaultCommissionRate);

        const estimatedCommission =
          rate === null
            ? 0
            : +((totalSales * rate) / 100).toFixed(2);

        return {
          ...record,
          matchedEventReportCount: matchedReports.length,
          packagesSold,
          totalSales,
          estimatedCommission,
        };
      });
    },
  },

  async mounted() {
    const startedAt = performance.now();

    await Promise.all([
      this.getItems(),
      this.getAuctioneerRecords(),
      this.getEventReports(),
    ]);

    this.reportingLoadMs = Math.round(
      performance.now() - startedAt,
    );

    this.reportingReady = true;

    console.log(
      `[AuctioneerRecords] Initial data + reporting loaded in ${this.reportingLoadMs}ms`,
    );
  },

  methods: {
    async getItems() {
      this.loading = true;

      try {
        const snapshot = await portalsApp
          .firestore()
          .collection("auctioneers")
          .orderBy("salesYtd", "desc")
          .get();

        const auctioneers = snapshot.docs.map((doc) => {
          const data = doc.data();

          return {
            ...data,
            id: data.id || doc.id,
            auctioneers: Array.isArray(data.auctioneers)
              ? data.auctioneers
              : [],
            salesYtd: Number(data.salesYtd) || 0,
          };
        });

        this.auctioneers = auctioneers.map((item) => {
          return {
            ...item,
            auctioneerNames: this.getAuctioneerNames(
              item.auctioneers,
              auctioneers,
            ),
          };
        });
      } catch (error) {
        console.error("Error loading auctioneers:", error);

        this.$toast.error("Unable to load legacy auctioneers", {
          timeout: 3000,
        });
      } finally {
        this.loading = false;
      }
    },

    async getAuctioneerRecords() {
      try {
        const snapshot = await portalsApp
          .firestore()
          .collection("auctioneerRecords")
          .get();

        const records = snapshot.docs.map((doc) => {
          const data = doc.data();

          return {
            ...data,
            id: data.id || doc.id,
            companyIds: Array.isArray(data.companyIds) ? data.companyIds : [],
            active:
              data.active === undefined || data.active === null
                ? true
                : data.active,
            defaultCommissionRate:
              data.defaultCommissionRate === undefined ||
              data.defaultCommissionRate === null ||
              data.defaultCommissionRate === ""
                ? null
                : Number(data.defaultCommissionRate),
          };
        });

        this.auctioneerRecords = records.map((item) => {
          return {
            ...item,
            companyNames: this.getCompanyNames(item.companyIds, records),
          };
        });
      } catch (error) {
        console.error("Error loading AuctioneerRecords:", error);

        this.$toast.error("Unable to load new auctioneer records", {
          timeout: 3000,
        });
      }
    },

    getAuctioneerNames(auctioneerIds, auctioneers) {
      if (!Array.isArray(auctioneerIds)) return [];

      return auctioneerIds
        .map((auctioneerId) => {
          const auctioneer = auctioneers.find(
            (item) => item.id === auctioneerId,
          );

          return auctioneer ? auctioneer.name : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
    },

    getCompanyNames(companyIds, records) {
      if (!Array.isArray(companyIds)) return [];

      return companyIds
        .map((companyId) => {
          const company = records.find(
            (item) => item.id === companyId && item.type === "company",
          );

          return company ? company.name : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
    },

    async getEventReports() {
      this.reportingLoading = true;

      const startedAt = performance.now();

      try {
        const snapshot = await portalsApp
          .firestore()
          .collection("eventReports")
          .get();

        this.allEventReports = snapshot.docs.map((doc) => {
          const data = doc.data();

          return {
            ...data,
            id: data.id || doc.id,
            eventReportTotal:
              Number(data.eventReportTotal) || 0,
            numberOfWinners:
              Number(data.numberOfWinners) || 0,
          };
        });

        const elapsed = Math.round(
          performance.now() - startedAt,
        );

        console.log(
          `[AuctioneerRecords] Loaded ${this.allEventReports.length} Event Reports in ${elapsed}ms`,
        );
      } catch (error) {
        console.error(
          "Error loading Event Reports:",
          error,
        );

        this.$toast.error(
          "Unable to load auctioneer reporting data",
          {
            timeout: 3000,
          },
        );
      } finally {
        this.reportingLoading = false;
      }
    },

    clearReportFilters() {
      this.reportYear = null;
      this.reportQuarter = null;
      this.reportMonth = null;
    },

    normalizeName(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
    },

    getEventDateParts(value) {
      if (!value) {
        return {
          year: "",
          month: "",
          monthName: "",
          quarter: "",
        };
      }

      const parts = String(value).split("-");

      if (parts.length !== 3) {
        return {
          year: "",
          month: "",
          monthName: "",
          quarter: "",
        };
      }

      const year = Number(parts[0]);
      const month = Number(parts[1]);

      if (!year || !month || month < 1 || month > 12) {
        return {
          year: "",
          month: "",
          monthName: "",
          quarter: "",
        };
      }

      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      return {
        year,
        month,
        monthName: monthNames[month - 1],
        quarter: Math.ceil(month / 3),
        quarterLabel: `Q${Math.ceil(month / 3)}`,
      };
    },

    getPackageCount(report) {
      if (!report) return 0;

      const storedCount = Number(report.numberOfWinners);

      if (!Number.isNaN(storedCount) && storedCount > 0) {
        return storedCount;
      }

      if (Array.isArray(report.winners)) {
        return report.winners.length;
      }

      if (Array.isArray(report.winnerPackageIDs)) {
        return report.winnerPackageIDs.length;
      }

      return 0;
    },

    getMatchedEventReports(record, records, eventReports) {
      if (!record || !record.name) return [];

      const names = [
        this.normalizeName(record.name),
      ];

      if (record.type === "company") {
        records
          .filter((item) => {
            return (
              item.type === "auctioneer" &&
              Array.isArray(item.companyIds) &&
              item.companyIds.includes(record.id)
            );
          })
          .forEach((item) => {
            const normalized = this.normalizeName(item.name);

            if (normalized) {
              names.push(normalized);
            }
          });
      }

      const matchingNames = [...new Set(names.filter(Boolean))];

      const matches = eventReports.filter((report) => {
        if (!report || report.archived === true) {
          return false;
        }

        const auctioneerName = this.normalizeName(
          report.auctioneerName,
        );

        const auctioneerCompany = this.normalizeName(
          report.auctioneerCompany,
        );

        return (
          matchingNames.includes(auctioneerName) ||
          matchingNames.includes(auctioneerCompany)
        );
      });

      const uniqueReports = new Map();

      matches.forEach((report) => {
        const key =
          report.id ||
          String(report.eventReportNumber || "");

        if (!key) return;

        if (!uniqueReports.has(key)) {
          uniqueReports.set(key, report);
        }
      });

      return Array.from(uniqueReports.values());
    },

    buildAggregateRows(detailRows, periodType) {
      const grouped = new Map();

      detailRows.forEach((row) => {
        let period = "";

        if (periodType === "year") {
          period = row.Year;
        } else if (periodType === "quarter") {
          period = row.Year && row.Quarter
            ? `${row.Year} ${row.Quarter}`
            : "";
        } else if (periodType === "month") {
          period = row.Year && row.Month
            ? `${row.Year} ${row.Month}`
            : "";
        }

        if (!period) return;

        const key = [
          row["Record ID"],
          period,
        ].join("|");

        if (!grouped.has(key)) {
          grouped.set(key, {
            "Record Type": row["Record Type"],
            "Auctioneer / Company": row["Auctioneer / Company"],
            Companies: row.Companies,
            Period: period,
            "Event Reports": 0,
            "Packages Sold": 0,
            "Total Sales": 0,
            "Default Commission Rate": row["Default Commission Rate"],
            "Estimated Commission": 0,
          });
        }

        const item = grouped.get(key);

        item["Event Reports"] += 1;
        item["Packages Sold"] += Number(row["Packages Sold"]) || 0;
        item["Total Sales"] += Number(row["Invoice Total"]) || 0;
        item["Estimated Commission"] +=
          Number(row["Estimated Commission"]) || 0;
      });

      return Array.from(grouped.values()).sort((a, b) => {
        const nameCompare =
          (a["Auctioneer / Company"] || "").localeCompare(
            b["Auctioneer / Company"] || "",
          );

        if (nameCompare !== 0) return nameCompare;

        return String(a.Period || "").localeCompare(
          String(b.Period || ""),
        );
      });
    },

    setSheetColumnWidths(sheet, widths) {
      sheet["!cols"] = widths.map((wch) => ({ wch }));
    },

    async exportAuctioneerReport() {
      if (this.exporting || !this.auctioneerRecords.length) {
        return;
      }

      this.exporting = true;

      try {
        const eventReports = this.allEventReports.length
          ? this.allEventReports
          : await (async () => {
              const snapshot = await portalsApp
                .firestore()
                .collection("eventReports")
                .get();

              return snapshot.docs.map((doc) => {
                const data = doc.data();

                return {
                  ...data,
                  id: data.id || doc.id,
                  eventReportTotal:
                    Number(data.eventReportTotal) || 0,
                  numberOfWinners:
                    Number(data.numberOfWinners) || 0,
                };
              });
            })();

        const records = this.auctioneerRecords;
        const summaryRows = [];
        const detailRows = [];

        records.forEach((record) => {
          const matchedReports =
            this.getMatchedEventReports(
              record,
              records,
              eventReports,
            );

          const defaultCommissionRate =
            record.defaultCommissionRate === undefined ||
            record.defaultCommissionRate === null ||
            record.defaultCommissionRate === ""
              ? null
              : Number(record.defaultCommissionRate);

          let totalSales = 0;
          let packagesSold = 0;
          let estimatedCommission = 0;

          matchedReports.forEach((report) => {
            const invoiceTotal =
              Number(report.eventReportTotal) || 0;

            const packageCount =
              this.getPackageCount(report);

            const commission =
              defaultCommissionRate === null
                ? 0
                : +(
                    (invoiceTotal * defaultCommissionRate) /
                    100
                  ).toFixed(2);

            totalSales += invoiceTotal;
            packagesSold += packageCount;
            estimatedCommission += commission;

            const dateParts =
              this.getEventDateParts(report.eventDate);

            detailRows.push({
              "Record ID": record.id,
              "Record Type":
                record.type === "company"
                  ? "Company"
                  : "Auctioneer",
              "Auctioneer / Company": record.name || "",
              Companies:
                Array.isArray(record.companyNames)
                  ? record.companyNames.join(", ")
                  : "",
              "Default Commission Rate":
                defaultCommissionRate === null
                  ? ""
                  : defaultCommissionRate,
              "Invoice Number":
                report.eventReportNumber || "",
              "Event Date": report.eventDate || "",
              Year: dateParts.year || "",
              Quarter: dateParts.quarterLabel || "",
              Month: dateParts.monthName || "",
              "Event Name": report.eventName || "",
              Organization: report.organizationName || "",
              "Packages Sold": packageCount,
              "Invoice Total": invoiceTotal,
              "Estimated Commission": commission,
              Paid: report.isPaid === true ? "Yes" : "No",
              "Date Paid": report.datePaid || "",
              "Legacy Auctioneer Name":
                report.auctioneerName || "",
              "Legacy Auctioneer Company":
                report.auctioneerCompany || "",
              "Event Report ID": report.id || "",
            });
          });

          summaryRows.push({
            "Record Type":
              record.type === "company"
                ? "Company"
                : "Auctioneer",
            "Auctioneer / Company": record.name || "",
            Companies:
              Array.isArray(record.companyNames)
                ? record.companyNames.join(", ")
                : "",
            Status: record.active ? "Active" : "Inactive",
            "Default Commission Rate":
              defaultCommissionRate === null
                ? ""
                : defaultCommissionRate,
            "Matched Event Reports": matchedReports.length,
            "Packages Sold": packagesSold,
            "Total Sales": totalSales,
            "Estimated Commission":
              defaultCommissionRate === null
                ? ""
                : +estimatedCommission.toFixed(2),
          });
        });

        summaryRows.sort((a, b) => {
          const typeCompare =
            (a["Record Type"] || "").localeCompare(
              b["Record Type"] || "",
            );

          if (typeCompare !== 0) return typeCompare;

          return (
            a["Auctioneer / Company"] || ""
          ).localeCompare(
            b["Auctioneer / Company"] || "",
          );
        });

        detailRows.sort((a, b) => {
          const nameCompare =
            (a["Auctioneer / Company"] || "").localeCompare(
              b["Auctioneer / Company"] || "",
            );

          if (nameCompare !== 0) return nameCompare;

          return String(b["Event Date"] || "").localeCompare(
            String(a["Event Date"] || ""),
          );
        });

        const yearRows =
          this.buildAggregateRows(detailRows, "year");

        const quarterRows =
          this.buildAggregateRows(detailRows, "quarter");

        const monthRows =
          this.buildAggregateRows(detailRows, "month");

        const workbook = XLSX.utils.book_new();

        const summarySheet =
          XLSX.utils.json_to_sheet(summaryRows);

        const detailSheet =
          XLSX.utils.json_to_sheet(detailRows);

        const yearSheet =
          XLSX.utils.json_to_sheet(yearRows);

        const quarterSheet =
          XLSX.utils.json_to_sheet(quarterRows);

        const monthSheet =
          XLSX.utils.json_to_sheet(monthRows);

        this.setSheetColumnWidths(
          summarySheet,
          [14, 32, 32, 12, 22, 20, 16, 16, 22],
        );

        this.setSheetColumnWidths(
          detailSheet,
          [
            22, 14, 32, 30, 22, 16, 14, 10, 10, 14,
            32, 32, 16, 16, 22, 10, 14, 30, 30, 28,
          ],
        );

        this.setSheetColumnWidths(
          yearSheet,
          [14, 32, 30, 14, 16, 16, 16, 22, 22],
        );

        this.setSheetColumnWidths(
          quarterSheet,
          [14, 32, 30, 14, 16, 16, 16, 22, 22],
        );

        this.setSheetColumnWidths(
          monthSheet,
          [14, 32, 30, 18, 16, 16, 16, 22, 22],
        );

        XLSX.utils.book_append_sheet(
          workbook,
          summarySheet,
          "Summary",
        );

        XLSX.utils.book_append_sheet(
          workbook,
          detailSheet,
          "Event Reports",
        );

        XLSX.utils.book_append_sheet(
          workbook,
          yearSheet,
          "Sales by Year",
        );

        XLSX.utils.book_append_sheet(
          workbook,
          quarterSheet,
          "Sales by Quarter",
        );

        XLSX.utils.book_append_sheet(
          workbook,
          monthSheet,
          "Sales by Month",
        );

        const now = new Date();

        const fileDate = [
          now.getFullYear(),
          String(now.getMonth() + 1).padStart(2, "0"),
          String(now.getDate()).padStart(2, "0"),
        ].join("");

        XLSX.writeFile(
          workbook,
          `auctioneer_report_${fileDate}.xlsx`,
        );

        this.$toast.success(
          "Auctioneer report exported",
          {
            timeout: 2500,
          },
        );
      } catch (error) {
        console.error(
          "Error exporting auctioneer report:",
          error,
        );

        this.$toast.error(
          "Unable to export auctioneer report",
          {
            timeout: 3000,
          },
        );
      } finally {
        this.exporting = false;
      }
    },

    openAuctioneer(item) {
      if (!item || !item.id) return;

      this.$router.push({
        path: `/auctioneerrecords/${item.id}`,
        query: {
          source: this.recordSource,
        },
      });
    },

    openNewAuctioneer() {
      this.resetNewItem();
      this.newDialog = true;
    },

    closeNewAuctioneer() {
      if (this.saving) return;

      this.newDialog = false;
      this.resetNewItem();
    },

    resetNewItem() {
      this.newItem = {
        type: "auctioneer",
        active: true,
        name: "",
        companyIds: [],
        phoneNumber: "",
        contactEmail: "",
        commissionEmail: "",
        defaultCommissionRate: null,
        mailingAddress: "",
        salesRep: "",
        notes: "",
      };
    },

    async createAuctioneerRecord(payload) {
      if (!this.canEditAuctioneers) return;

      this.saving = true;

      try {
        const now = new Date();

        const record = {
          ...payload,
          createdAt: now,
          updatedAt: now,
        };

        const newDoc = await portalsApp
          .firestore()
          .collection("auctioneerRecords")
          .add(record);

        await portalsApp
          .firestore()
          .collection("auctioneerRecords")
          .doc(newDoc.id)
          .update({
            id: newDoc.id,
          });

        this.$toast.success(
          payload.type === "company"
            ? "Auctioneer company created"
            : "Auctioneer created",
          {
            timeout: 2000,
          },
        );

        await this.getAuctioneerRecords();

        this.newDialog = false;
        this.resetNewItem();

        // Automatically switch to the new data so the user
        // immediately sees the record they just created.
        this.recordSource = "new";
      } catch (error) {
        console.error("Error creating auctioneer record:", error);

        this.$toast.error("Unable to create auctioneer record", {
          timeout: 3000,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>